import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';
import { publicApi } from '../utils/api';
import { track, CONTACT_SUBMIT } from '../utils/analytics';

const ORG_TYPES = ['Club', 'Academy', 'Federation', 'School or university', 'Coaching organization', 'Other'];

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-navy-900 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400";

export default function Contact() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isFederationDemo = ['federation', 'enterprise'].includes(params.get('plan'));

  usePageMeta(
    isFederationDemo ? 'Tactmark Federation Demo | Tactmark' : 'Contact | Tactmark',
    isFederationDemo
      ? 'Book a Tactmark Federation demo for football associations and multi-team development programs.'
      : 'Get in touch with the Tactmark team about your club, academy, or organization.'
  );

  const [form, setForm] = useState({
    name: '', organization: '', email: '', message: '',
    country: '', num_teams: '', num_staff: '', organization_type: '',
    current_analysis_process: '', main_operational_problem: '', desired_timing: '',
    procurement_requirements: '',
    plan: isFederationDemo ? 'federation' : '',
    lead_source: (() => {
      if (!isFederationDemo) return 'contact_page';
      const from = params.get('from');
      if (from === 'pricing') return 'pricing_page';
      if (from === 'home') return 'home_page';
      if (from === 'federation_product_page') return 'federation_product_page';
      return from || 'federation_product_page';
    })(),
  });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');

  // Sync plan and lead_source when URL query changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const federation = ['federation', 'enterprise'].includes(params.get('plan'));
    const from = params.get('from');
    let leadSource = 'contact_page';
    if (federation) {
      if (from === 'pricing') leadSource = 'pricing_page';
      else if (from === 'home') leadSource = 'home_page';
      else if (from === 'federation_product_page') leadSource = 'federation_product_page';
      else leadSource = from || 'federation_product_page';
    }
    setForm((f) => ({
      ...f,
      plan: federation ? 'federation' : '',
      lead_source: leadSource,
    }));
  }, [location.search]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.message) {
      setError('Please fill in your name, email, and message.');
      return;
    }
    setStatus('submitting');
    try {
      const result = await publicApi.submitContact(form);
      track(CONTACT_SUBMIT, {
        plan: form.plan || 'contact',
        lead_source: form.lead_source || 'contact_page',
      });
      setStatus('done');
      setDeliveryNote(result.delivery === 'emailed'
        ? 'Your request was delivered to the Tactmark team.'
        : 'Your request was saved securely and is available to the Tactmark team.');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <div className="max-w-2xl mx-auto px-6 py-16">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">
          {isFederationDemo ? 'Tactmark Federation' : 'Contact'}
        </p>
        <h1 className="font-serif text-4xl text-navy-900 leading-tight mb-4">
          {isFederationDemo ? 'Book a Federation Demo' : 'Talk to us.'}
        </h1>
        <p className="text-gray-600 leading-relaxed mb-10">
          {isFederationDemo
            ? 'Tell us about your football association or multi-team development program. Federation deployments are configured around participating teams, technical objectives, reporting requirements, and staff access.'
            : 'For product questions about Post-Match or Federation, organization support, or anything else, reach out and we\'ll respond directly. You can also email '}
          {!isFederationDemo && (
            <a href="mailto:hello@tactmark.com" className="text-navy-700 underline">hello@tactmark.com</a>
          )}
          {!isFederationDemo && '.'}
        </p>

        {status === 'done' ? (
          <div className="bg-navy-50 border border-navy-100 rounded-2xl p-8 text-center">
            <p className="text-navy-900 font-semibold mb-1">
              {isFederationDemo ? 'Thank you for your Federation demo request.' : 'Thanks, your message was received.'}
            </p>
            <p className="text-gray-500 text-sm">{deliveryNote}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
            )}

            <Field label="Name" required>
              <input value={form.name} onChange={set('name')} className={inputCls} />
            </Field>
            <Field label="Work email" required>
              <input type="email" value={form.email} onChange={set('email')} className={inputCls} />
            </Field>
            <Field label="Organization">
              <input value={form.organization} onChange={set('organization')} className={inputCls} />
            </Field>

            {isFederationDemo && (
              <>
                <Field label="Country">
                  <input value={form.country} onChange={set('country')} placeholder="e.g. United Kingdom" className={inputCls} />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Number of teams">
                    <input type="number" min="2" value={form.num_teams} onChange={set('num_teams')} placeholder="e.g. 4" className={inputCls} />
                  </Field>
                  <Field label="Estimated staff users">
                    <input type="number" min="1" value={form.num_staff} onChange={set('num_staff')} placeholder="e.g. 12" className={inputCls} />
                  </Field>
                </div>
                <Field label="Organization type">
                  <select value={form.organization_type} onChange={set('organization_type')} className={inputCls}>
                    <option value="">Select…</option>
                    {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Current development reporting process">
                  <input value={form.current_analysis_process} onChange={set('current_analysis_process')} placeholder="How programs currently submit updates and how technical leadership reviews them" className={inputCls} />
                </Field>
                <Field label="Main operational problem">
                  <input value={form.main_operational_problem} onChange={set('main_operational_problem')} placeholder="What technical leadership cannot see today" className={inputCls} />
                </Field>
                <Field label="Desired implementation timing">
                  <input value={form.desired_timing} onChange={set('desired_timing')} placeholder="e.g. Next quarter" className={inputCls} />
                </Field>
                <Field label="Procurement requirements (optional)">
                  <input value={form.procurement_requirements} onChange={set('procurement_requirements')} placeholder="Security questionnaire, MSA, etc." className={inputCls} />
                </Field>
              </>
            )}

            <Field label="Message" required>
              <textarea
                rows={5}
                value={form.message}
                onChange={set('message')}
                placeholder={isFederationDemo ? 'Tell us about your organization, development programs, reporting process, and what you are looking for.' : ''}
                className={inputCls}
              />
            </Field>

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="bg-navy-900 hover:bg-navy-800 disabled:opacity-60 text-white font-medium px-8 py-3 rounded transition-colors text-sm"
            >
              {status === 'submitting' ? 'Sending…' : isFederationDemo ? 'Book a Federation Demo' : 'Send message'}
            </button>
          </form>
        )}
      </div>

      <PublicFooter />
    </div>
  );
}
